import type { EventOutcome, PredictionEventCatalog, PredictionMarket } from '../types/event'
import type { PresentationOutcomeStructure } from '../types/presentation'

const MONTHS: Record<string, string> = {
  january: '1月', february: '2月', march: '3月', april: '4月', may: '5月', june: '6月',
  july: '7月', august: '8月', september: '9月', october: '10月', november: '11月', december: '12月',
  jan: '1月', feb: '2月', mar: '3月', apr: '4月', jun: '6月', jul: '7月', aug: '8月',
  sep: '9月', sept: '9月', oct: '10月', nov: '11月', dec: '12月',
}

function normalized(value: unknown) {
  return String(value ?? '').normalize('NFKC').replace(/[’‘]/g, "'").replace(/\s+/g, ' ').trim()
}

function lower(value: unknown) {
  return normalized(value).toLowerCase()
}

function marketQuestion(market: PredictionMarket) {
  return normalized(market.originalTitle ?? market.title)
}

function linkedMarkets(event: PredictionEventCatalog, outcome: EventOutcome) {
  const ids = new Set(outcome.marketIds)
  return event.markets.filter((market) => ids.has(market.id))
}

function dateLabel(value: string) {
  const match = value.match(/\b(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec)\s+(\d{1,2})(?:,\s*\d{4})?/i)
  if (match) return `${MONTHS[match[1].toLowerCase()]}${match[2]}日`
  const chinese = value.match(/(\d{1,2})月\s*(\d{1,2})日/u)
  return chinese ? `${chinese[1]}月${chinese[2]}日` : undefined
}

function entityLabel(value: string, fallback?: string) {
  if (/u\.?s\.?\s+dollar\s+index|\bdxy\b|美元指数/iu.test(value)) return '美元指数（DXY）'
  if (/s\s*&\s*p\s*500|标普\s*500/iu.test(value)) return '标普500指数'
  if (/spacex.*starship.*flight\s+test\s*14|星舰.*14.*飞行测试/iu.test(value)) return 'SpaceX 星舰第14次飞行测试'
  if (/spacex.*starship|星舰/iu.test(value)) return 'SpaceX 星舰'
  if (/\bbitcoin\b|比特币/iu.test(value)) return '比特币'
  if (/\beth(?:ereum)?\b|以太坊/iu.test(value)) return '以太坊'
  if (/\bgold\b|黄金/iu.test(value)) return '黄金'
  return fallback
}

function directionMarketEntity(value: string) {
  if (/microsoft/i.test(value)) return '微软（MSFT）'
  if (/nvidia/i.test(value)) return '英伟达（NVDA）'
  if (/dogecoin/i.test(value)) return '狗狗币'
  if (/\bxrp\b/i.test(value)) return 'XRP'
  if (/solana/i.test(value)) return 'Solana'
  if (/tesla/i.test(value)) return '特斯拉（TSLA）'
  return entityLabel(value, value.replace(/\s+/g, ' ').trim())
}

function directionLabel(value: string) {
  if (/\b(?:above|over|higher|up)\b|高于|超过|上涨|上升/iu.test(value)) return '以上'
  if (/\b(?:below|under|lower|down)\b|低于|下方|下跌|下降/iu.test(value)) return '以下'
  return undefined
}

function thresholdQuestion(question: string) {
  const match = question.match(/^Will\s+(?:the\s+)?(.+?)\s+be\s+(above|below|over|under)\s+([\d,.]+)(.*?)[?？]?$/i)
  if (!match) return undefined
  const entity = entityLabel(match[1])
  if (!entity) return undefined
  const direction = directionLabel(match[2])
  if (!direction) return undefined
  const date = dateLabel(match[4])
  return `${entity}在${match[3]}${direction}吗？${date ? `（${date}）` : ''}`
}

function semanticQuestionTranslation(question: string, event: PredictionEventCatalog, displayTitle?: string) {
  const source = normalized(question)
  if (!source) return undefined

  const ballotText = [event.originalTitle, event.title, event.nativeEventTitle].filter(Boolean).join(' ')
  const ballotPeriod = ballotText.match(/\((\d{1,2})\/(\d{1,2})\s*-\s*(\d{1,2})\/(\d{1,2})\)/)
  const ballotThreshold = source.match(/\b(?:above|over)\s+([\d,.]+)%/i)?.[1]
  if (/generic ballot.*democrats.*up\/down/iu.test(ballotText) && ballotPeriod && ballotThreshold) {
    return `民主党在${ballotPeriod[1]}月${ballotPeriod[2]}日至${ballotPeriod[3]}月${ballotPeriod[4]}日期间的支持率会高于${ballotThreshold}%吗？`
  }

  const upDown = source.match(/^(?:Will\s+)?(.+?)\s+up\s+or\s+down\s+(?:(?:on)\s+|-\s*)(.+?)[?？]?$/i)
  if (upDown) {
    const entity = directionMarketEntity(upDown[1])
    const date = dateLabel(upDown[2])
    const time = upDown[2].match(/\b\d{1,2}(?::\d{2})?\s*(?:AM|PM)(?:\s*-\s*\d{1,2}(?::\d{2})?\s*(?:AM|PM))?\s*ET\b/i)?.[0]
    return `${entity}${date ? `在${date}${time ? ` ${time}` : ''}` : `在${upDown[2]}`}会涨还是跌？`
  }

  const threshold = thresholdQuestion(source)
  if (threshold) return threshold

  if (/SpaceX\s+Starship\s+Flight\s+Test\s*14/i.test(source) && /\blaunch\s+by\b/i.test(source)) {
    const date = dateLabel(source)
    return `SpaceX 星舰第14次飞行测试会在${date ? `${date}前` : '截止日前'}发射吗？`
  }
  if (/SpaceX\s+Starship\s+Flight\s+Test\s*14/i.test(source) && /superheavy\s+explode/i.test(source)) {
    return 'SpaceX星舰第14次飞行测试的超重型助推器会发生爆炸吗？'
  }
  if (/SpaceX\s+Starship\s+Flight\s*Test\s*14/i.test(source) && /chopsticks\s+catch.*superheavy/i.test(source)) {
    return 'SpaceX星舰第14次飞行测试中，发射台机械臂会接住超重型助推器吗？'
  }
  if (/SpaceX\s+Starship\s+Flight\s*Test\s*14/i.test(source) && /chopsticks\s+catch/i.test(source)) {
    return 'SpaceX星舰第14次飞行测试中，发射台机械臂会接住星舰吗？'
  }
  if (/SpaceX\s+Starship\s+Flight\s*Test\s*14/i.test(source) && /successful\s+splas?hdown/i.test(source)) {
    return 'SpaceX星舰第14次飞行测试会成功溅落吗？'
  }

  const baseEntity = entityLabel(source, entityLabel(displayTitle ?? '', event.nativeEventTitle ?? event.title))
  if (baseEntity && /\b(?:launch|release|arrive)\s+by\b/i.test(source)) {
    const date = dateLabel(source)
    return `${baseEntity}会在${date ? `${date}前` : '截止日前'}完成相关目标吗？`
  }
  return undefined
}

function looksLikeCompleteQuestion(value: string) {
  return /\?|？|^will\b|^whether\b|吗/u.test(value.trim())
}

function isBareOutcomeLabel(value: string) {
  const candidate = normalized(value)
  return /^(?:yes|no|other|up|down|above|below|daily\s+up\/down|(?:january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{1,2}|\d{1,2}月\d{1,2}日)$/iu.test(candidate)
}

export function outcomeSourceTitle(event: PredictionEventCatalog, outcome: EventOutcome) {
  const source = linkedMarkets(event, outcome).map(marketQuestion).find(looksLikeCompleteQuestion)
  return source ?? linkedMarkets(event, outcome).map(marketQuestion).find(Boolean) ?? normalized(outcome.label)
}

export function localDisplayOutcomeLabel(
  event: PredictionEventCatalog,
  outcome: EventOutcome,
  displayTitle?: string,
) {
  const rawLabel = normalized(outcome.label)
  const source = outcomeSourceTitle(event, outcome)
  const translated = semanticQuestionTranslation(source, event, displayTitle)
  if (translated) return translated
  if (!isBareOutcomeLabel(rawLabel)) return rawLabel
  if (looksLikeCompleteQuestion(source) && source !== rawLabel) return source
  const context = displayTitle ?? event.originalTitle ?? event.title
  return context ? `${context}：${rawLabel}` : rawLabel
}

export function inferOutcomeStructure(event: PredictionEventCatalog): PresentationOutcomeStructure {
  if (event.outcomes.length < 2) return 'unknown'
  const probabilities = event.outcomes
    .map((outcome) => outcome.probability)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
  const sum = probabilities.reduce((total, value) => total + value, 0)
  if (probabilities.length >= 2 && sum > 1.15) return 'independent-conditions'
  if (event.eventType === 'multiOutcome') return 'mutually-exclusive'
  return 'unknown'
}

export function outcomeStructureLabel(structure: PresentationOutcomeStructure) {
  if (structure === 'independent-conditions') return '相关预测概率'
  return '结果概率分布'
}

export function outcomeStructureNote(structure: PresentationOutcomeStructure) {
  return structure === 'independent-conditions'
    ? '这些预测彼此独立，概率不一定合计100%。'
    : undefined
}

function predicateLabel(value: string) {
  if (/above|below|over|under|higher|lower|up|down|高于|低于|上涨|下跌/iu.test(value)) return 'comparison'
  if (/launch|flight\s+test|发射|飞行测试/iu.test(value)) return 'flight-test'
  if (/explode|爆炸/iu.test(value)) return 'explosion'
  if (/win|winner|获胜|冠军/iu.test(value)) return 'winner'
  if (/buy|purchase|购买/iu.test(value)) return 'purchase'
  if (/sell|selling|出售/iu.test(value)) return 'sale'
  return 'question'
}

function numericMarkers(value: string) {
  return [...value.matchAll(/\d+(?:[.,]\d+)?/g)].map((match) => match[0]).join(',')
}

export function buildDisplaySemanticKey(event: PredictionEventCatalog): string {
  const markets = event.markets.map(marketQuestion).filter(Boolean)
  const text = [event.originalTitle, event.title, event.nativeEventTitle, event.nativeSeriesTitle, ...markets]
    .filter(Boolean)
    .join(' ')
  const structure = inferOutcomeStructure(event)
  const entity = entityLabel(text, normalized(event.nativeEventTitle ?? event.nativeSeriesTitle ?? event.title))
  const direction = directionLabel(text) ?? 'none'
  const threshold = markets.map(numericMarkers).filter(Boolean).join('|') || 'none'
  const range = text.match(/(?:between|from)\s+([^?]+?)\s+(?:and|to)\s+([^?]+)/i)?.[0] ?? 'none'
  const deadline = [event.deadline, ...markets.map(dateLabel).filter(Boolean)].filter(Boolean).join('|') || 'none'
  const outcome = event.outcomes.map((outcome) => lower(outcome.label)).sort().join('|') || 'none'
  return [
    `entity=${lower(entity)}`,
    `predicate=${predicateLabel(text)}`,
    `outcome=${outcome}`,
    `direction=${lower(direction)}`,
    `threshold=${threshold}`,
    `range=${lower(range)}`,
    `deadline=${lower(deadline)}`,
    `answerType=${event.eventType}:${structure}`,
  ].join(';')
}

export function semanticTitleCandidate(event: PredictionEventCatalog, baseTitle: string) {
  const directionText = `${baseTitle} ${event.title} ${event.originalTitle ?? ''}`
  if (!/daily\s+up\/down|up\/down|up\s*(?:or|或)\s*down/iu.test(directionText)) return undefined
  const source = event.markets.map(marketQuestion).find(looksLikeCompleteQuestion) ?? event.markets.map(marketQuestion).find(Boolean)
  if (!source) return undefined
  return semanticQuestionTranslation(source, event, baseTitle) ?? source
}

export function presentationTitleForEvent(event: PredictionEventCatalog, baseTitle: string) {
  const candidate = semanticTitleCandidate(event, baseTitle)
  return candidate ?? baseTitle
}

function semanticDisambiguator(event: PredictionEventCatalog) {
  const source = event.markets.map(marketQuestion).find(Boolean) ?? ''
  const threshold = source.match(/\b(?:above|below|over|under)\s+([\d,.]+)/i)?.[1]
  const direction = source.match(/\b(?:above|over|higher|up|below|under|lower|down)\b/i)?.[0]
  const date = dateLabel(source)
  const deadline = event.deadline ?? event.markets.map((market) => market.endTime).find(Boolean)
  const pieces = [threshold ? `${threshold}${direction && /below|under|lower|down/i.test(direction) ? '以下' : '以上'}` : undefined, date]
    .filter(Boolean)
  return pieces.join(' · ') || (deadline ? `截止${deadline.replace('T', ' ').slice(0, 16)}` : undefined) || event.outcomes[0]?.label || event.id
}

export function ensureUniquePresentationTitles(
  events: PredictionEventCatalog[],
  baseTitles: Map<string, string>,
) {
  const titles = new Map(events.map((event) => [event.id, baseTitles.get(event.id) ?? event.title ?? event.originalTitle ?? '未命名预测']))
  for (let pass = 0; pass < 2; pass += 1) {
    const groups = new Map<string, PredictionEventCatalog[]>()
    for (const event of events) {
      const title = lower(titles.get(event.id))
      const group = groups.get(title) ?? []
      group.push(event)
      groups.set(title, group)
    }
    for (const group of groups.values()) {
      const semanticKeys = new Set(group.map(buildDisplaySemanticKey))
      if (group.length < 2 || semanticKeys.size < 2) continue
      for (const event of group) {
        const current = titles.get(event.id) ?? event.title ?? event.originalTitle ?? '未命名预测'
        const candidate = semanticTitleCandidate(event, current)
        if (candidate && lower(candidate) !== lower(current)) titles.set(event.id, candidate)
      }
    }
  }

  const groups = new Map<string, PredictionEventCatalog[]>()
  for (const event of events) {
    const title = lower(titles.get(event.id))
    const group = groups.get(title) ?? []
    group.push(event)
    groups.set(title, group)
  }
  for (const group of groups.values()) {
    const semanticKeys = new Set(group.map(buildDisplaySemanticKey))
    if (group.length < 2 || semanticKeys.size < 2) continue
    for (const event of group) {
      const current = titles.get(event.id) ?? event.title ?? event.originalTitle ?? '未命名预测'
      titles.set(event.id, `${current}（${semanticDisambiguator(event)}）`)
    }
  }
  return titles
}
